import { Device } from "@fairfleet/geotab";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DeviceSelect } from "./components/DeviceSelect";
import { LiveDataChecklist } from "./components/LiveDataChecklist";
import { LiveDataGrid } from "./components/LiveDataGrid";
import { useLiveDataRows } from "./hooks/useLiveDataRows";
import { mapLazy } from "@/utilities/mapLazy";

const FilterStackLayout = mapLazy(
  "FilterStackLayout",
  import("@/components/layout/FilterStackLayout"),
);

const TIME_WINDOW_OPTIONS = [5, 10, 30] as const;
const REFRESH_INTERVAL_OPTIONS = [5, 10, 30, 60] as const;

export default function LiveDataPage() {
  const { t } = useTranslation();
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [minutesBack, setMinutesBack] = useState<number>(5);
  const [refreshInterval, setRefreshInterval] = useState<number>(10);

  const { rows, fallbackRows, isLoading, isFetching } = useLiveDataRows(
    selectedDevice?.id,
    minutesBack,
    refreshInterval * 1000,
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(700));

  return (
    <FilterStackLayout
      isMobile={isMobile}
      minHeight={60}
      filters={
        <>
          <DeviceSelect value={selectedDevice} onChange={setSelectedDevice} />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t("liveData.timeWindow", "Time window")}</InputLabel>
            <Select
              value={minutesBack}
              label={t("liveData.timeWindow", "Time window")}
              onChange={e => setMinutesBack(e.target.value as number)}
            >
              {TIME_WINDOW_OPTIONS.map(min => (
                <MenuItem key={min} value={min}>
                  {t("liveData.minutes", "{{count}} minutes", { count: min })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t("liveData.refreshInterval", "Refresh")}</InputLabel>
            <Select
              value={refreshInterval}
              label={t("liveData.refreshInterval", "Refresh")}
              onChange={e => setRefreshInterval(e.target.value as number)}
            >
              {REFRESH_INTERVAL_OPTIONS.map(sec => (
                <MenuItem key={sec} value={sec}>
                  {t("liveData.seconds", "{{count}}s", { count: sec })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      }
      content={
        <>
          {/* Without a vehicle every line would read "not found", above the grid's own prompt. */}
          {selectedDevice && (
            <LiveDataChecklist device={selectedDevice} rows={rows} fallbackRows={fallbackRows} />
          )}
          <LiveDataGrid
            rows={rows}
            isLoading={isLoading}
            isFetching={isFetching}
            hasDevice={!!selectedDevice}
          />
        </>
      }
    />
  );
}
